/**
 * Chart Creator summary-table semantics.
 *
 * The template picks the five period slots from the chart's time range, and
 * both layouts are always written to the worksheet even though the chart shows
 * only one. Reproducing that rule here is what lets `read_native_chart` report
 * the current table and lets the model do relative edits ("swap L3M and L6M",
 * "remove the first period and move up the rest") without a round trip that
 * depends on cells we may not be able to read.
 *
 * Source: `f2_docs/charting/chart-creator-api-parameter-skill.md` §19.
 */

import type {
  FactsetChartSummaryTableDisplay,
  FactsetChartSummaryTablePeriod,
} from "./types";

export const SUMMARY_TABLE_DEFAULT_DISPLAY: FactsetChartSummaryTableDisplay =
  "Horizontal";

export const SUMMARY_TABLE_PERIOD_SLOTS = 5;

/** Slot value meaning "this period row is hidden". */
export const SUMMARY_TABLE_HIDDEN: FactsetChartSummaryTablePeriod = "No";

interface DefaultPeriodBand {
  /** Inclusive lower bound of the chart range, in whole months. */
  minMonths: number;
  periods: readonly FactsetChartSummaryTablePeriod[];
}

/**
 * Ordered longest-first so the first band whose `minMonths` fits wins.
 * Frequency has no effect on this choice; only the range length matters.
 */
const DEFAULT_PERIOD_BANDS: readonly DefaultPeriodBand[] = [
  { minMonths: 120, periods: ["Current", "L10Y", "L5Y", "L3Y", "L1Y"] },
  { minMonths: 60, periods: ["Current", "L5Y", "L3Y", "L1Y", "YTD"] },
  { minMonths: 36, periods: ["Current", "L3Y", "L1Y", "YTD", "L6M"] },
  { minMonths: 24, periods: ["Current", "L2Y", "L1Y", "YTD", "L6M"] },
  { minMonths: 12, periods: ["Current", "L1Y", "YTD", "L6M", "L3M"] },
  { minMonths: 6, periods: ["Current", "L6M", "L3M"] },
  { minMonths: 0, periods: ["Current", "L3M"] },
];

function padSlots(
  periods: readonly FactsetChartSummaryTablePeriod[],
): FactsetChartSummaryTablePeriod[] {
  const slots = periods.slice(0, SUMMARY_TABLE_PERIOD_SLOTS);
  while (slots.length < SUMMARY_TABLE_PERIOD_SLOTS) {
    slots.push(SUMMARY_TABLE_HIDDEN);
  }
  return slots;
}

function parseAbsoluteDate(value: string): Date | undefined {
  const trimmed = value.trim();
  let match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (match) {
    return new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]));
  }
  match = /^(\d{4})(\d{2})(\d{2})$/.exec(trimmed);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return undefined;
}

function resolveEndDate(endDate: string | undefined, now: Date): Date {
  const trimmed = (endDate ?? "").trim();
  if (!trimmed || /^now$/i.test(trimmed)) return now;
  return parseAbsoluteDate(trimmed) ?? now;
}

/**
 * Chart range in whole months, or `undefined` when the start date is an
 * open-ended sentinel we cannot measure. `ALL_DATA` and `P_FIRST_DATE` are
 * treated as the longest band rather than as unknown, since both always span
 * more than ten years in practice.
 */
export function summaryTableRangeMonths(
  startDate: string | undefined,
  endDate: string | undefined,
  now: Date = new Date(),
): number | undefined {
  const end = resolveEndDate(endDate, now);
  const start = (startDate ?? "").trim();
  if (!start) return undefined;
  if (/^(all_data|p_first_date)$/i.test(start)) return 120;
  if (/^ytd$/i.test(start)) {
    return end.getMonth();
  }
  const relative = /^-(\d+)\s*(AY|AM|AD|Y|M|D|W)$/i.exec(start);
  if (relative) {
    const amount = Number(relative[1]);
    const unit = relative[2].toUpperCase();
    if (unit === "AY" || unit === "Y") return amount * 12;
    if (unit === "AM" || unit === "M") return amount;
    if (unit === "W") return Math.round((amount * 7) / 30.4375);
    return Math.round(amount / 30.4375);
  }
  const absolute = parseAbsoluteDate(start);
  if (!absolute) return undefined;
  const months =
    (end.getFullYear() - absolute.getFullYear()) * 12 +
    (end.getMonth() - absolute.getMonth()) +
    (end.getDate() >= absolute.getDate() ? 0 : -1);
  return months < 0 ? 0 : months;
}

/**
 * The five period slots the template applies when nobody overrides them.
 * Returns `undefined` when the range cannot be measured, so callers report
 * "unknown" instead of a plausible-looking wrong table.
 */
export function defaultSummaryTablePeriods(
  startDate: string | undefined,
  endDate: string | undefined,
  now: Date = new Date(),
): FactsetChartSummaryTablePeriod[] | undefined {
  const months = summaryTableRangeMonths(startDate, endDate, now);
  if (months === undefined) return undefined;
  const band = DEFAULT_PERIOD_BANDS.find((entry) => months >= entry.minMonths);
  return band ? padSlots(band.periods) : undefined;
}

export interface RelativeSummaryEdit {
  swapPeriods?: readonly [number, number];
  removePeriods?: readonly number[];
  /**
   * Close the gaps left by `removePeriods`, which is what "remove the first
   * period and move the rest up" means as opposed to blanking a row in place.
   */
  compactPeriods?: boolean;
}

/** Slot indices are 1-based and there are only five of them. */
export function isSummaryTableSlotIndex(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= SUMMARY_TABLE_PERIOD_SLOTS
  );
}

/**
 * Apply a relative edit expressed as slot indices. Returns the full five-slot
 * result so the caller can send an absolute table, which is what both the
 * worksheet and the API expect. Out-of-range indices are ignored; callers
 * validate them at the tool boundary where a real error message is possible.
 *
 * Generic over the slot value because the worksheet's own vocabulary may not
 * match the API's: reordering the cells' current contents keeps a template we
 * have not yet fingerprinted intact, where rewriting them as API codes would
 * corrupt it.
 */
export function applyRelativeSlotEdit<T>(
  current: readonly T[],
  hidden: T,
  edit: RelativeSummaryEdit,
  isHidden: (value: T) => boolean = (value) => value === hidden,
): T[] {
  const slots = current.slice(0, SUMMARY_TABLE_PERIOD_SLOTS);
  while (slots.length < SUMMARY_TABLE_PERIOD_SLOTS) slots.push(hidden);
  if (edit.swapPeriods) {
    const [left, right] = edit.swapPeriods;
    if (isSummaryTableSlotIndex(left) && isSummaryTableSlotIndex(right)) {
      const held = slots[left - 1];
      slots[left - 1] = slots[right - 1];
      slots[right - 1] = held;
    }
  }
  for (const index of edit.removePeriods ?? []) {
    if (isSummaryTableSlotIndex(index)) slots[index - 1] = hidden;
  }
  if (!edit.compactPeriods) return slots;
  const remaining = slots.filter((slot) => !isHidden(slot));
  while (remaining.length < SUMMARY_TABLE_PERIOD_SLOTS) remaining.push(hidden);
  return remaining;
}
